import { HttpParams, HttpClient } from '@angular/common/http';
import { Injectable, effect, signal, computed, inject } from '@angular/core';
import { environment } from '@environment';
import {
  Transaction,
  LinePick,
  Question,
  QuestionType,
  QuestionOption,
  QuizPrizePackage,
  PrizePackageMode,
  HistoryRecord,
} from '@longnecktech/splash-commons-fe';
import { CurrencyService } from '@services/currency.service';
import { ResultsService } from '@services/results.service';
import { SessionService } from '@services/session.service';
import { PICK_QUESTION_TYPES } from '@shared/constants/game.consts';
import {
  isOptionWrong,
  isOptionPicked,
  isAnswerCorrect,
  isOptionCorrect,
} from '@shared/sections/my-picks/my-picks.utils';
import { Quiz } from '@longnecktech/splash-commons-fe';
import { Leaderboard } from '@shared/types/leaderboard';
import { ResultStatus } from '@shared/types/transaction-status';
import { overridePrizeLabel } from '@shared/utils/prize-label.utils';
import {
  tap,
  map,
  catchError,
  throwError,
  Observable,
  EMPTY,
  take,
  finalize,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TransactionService {
  private http = inject(HttpClient);
  private session = inject(SessionService);
  private resultsService = inject(ResultsService);
  private currencyService = inject(CurrencyService);

  // ----- CURRENT GAME DATA -----
  currentTransaction = signal<Transaction | undefined>(undefined);
  isTransactionLoaded = signal<boolean>(false);
  isTransactionEmpty = signal<boolean>(true);
  isTransactionNotEmpty = computed(() => !this.isTransactionEmpty());

  // ----- HISTORY GAMES DATA -----
  historyList = signal<HistoryRecord[]>([]);
  activeHistoryGameUuid = signal<string | undefined>(undefined);
  isLoading = signal<boolean>(false);
  isInitialized = signal<boolean>(false);
  selectedHistoryTx = signal<Transaction | undefined>(undefined);
  selectedHistoryGame = computed(() => this.selectedHistoryTx()?.quizGame);
  selectedHistoryLines = computed(
    () => this.selectedHistoryTx()?.quizLines || [],
  );
  selectedHistoryGameStatus = computed(() =>
    this.resultsService.getResultsStatus(this.selectedHistoryTx()),
  );
  allPickedOptions = computed(() =>
    this.getPickedOptions(this.selectedHistoryTx()),
  );
  totalStakeValue = computed(() =>
    this.resultsService.getTotalStakeValue(this.selectedHistoryTx()),
  );
  totalPrizes = computed(() =>
    this.resultsService.getTotalPrizes(this.selectedHistoryLines()),
  );

  private _historyGameCache = new Map<string, Transaction>();

  constructor() {
    effect(() => {
      const uuid = this.activeHistoryGameUuid();
      if (!uuid) return;

      const cached = this._historyGameCache.get(uuid);
      if (cached) {
        this.selectedHistoryTx.set(cached);
        return;
      }
      this.fetchGameHistoryByUuid(uuid).pipe(take(1)).subscribe();
    });
  }

  fetchHistoryList() {
    this.isLoading.set(true);
    let params = new HttpParams();
    if (this.session.sessionParams.instance) {
      params = params.append('instance', this.session.sessionParams.instance);
    }
    return this.http
      .get<HistoryRecord[]>(
        `${environment.backendUrl}/api/quiz/transaction/history/list`,
        {
          params,
        },
      )
      .pipe(
        tap((list) => {
          // we want the current game to be the first one in the history
          const currentGameName = this.session.game()?.name;
          if (currentGameName) {
            const currentGameIndex = list.findIndex(
              // TODO: return game uuid inside HistoryRecord (backend change)
              (i) => i.gameName === currentGameName,
            );
            // moves the current game to the front of historyList if it's
            // found further back, leaving order unchanged if it's already first or absent
            if (currentGameIndex > 0) {
              const [currentGame] = list.splice(currentGameIndex, 1);
              list.unshift(currentGame);
            }
          }
          // we want to limit history list to 10 games only (Jose request)
          const limitedList = list.slice(0, 10);
          this.historyList.set(limitedList);
          const firstUuid = limitedList[0]?.uuid;
          if (firstUuid) {
            this.activeHistoryGameUuid.set(firstUuid);
          } else {
            // initialized but empty
            this.isInitialized.set(true);
          }
        }),
        finalize(() => this.isLoading.set(false)),
      );
  }

  fetchGameHistoryByUuid(uuid: string) {
    this.isLoading.set(true);
    return this.http
      .get<Transaction>(
        `${environment.backendUrl}/api/quiz/transaction/history/${uuid}`,
      )
      .pipe(
        map((tx) => {
          return tx ? this.transformTransaction(tx) : tx;
        }),
        tap((tx) => {
          this.selectedHistoryTx.set(tx);
          if (tx.quizGame.uuid === this.session.game()?.uuid) {
            // this is legacy code
            this.session.updateCurrentGameResult(true);
            this.session.removeGameFromStorage();
          } else {
            // I don't want to add a current game tx to the cache because
            // we want to have the latest data always
            // we could edit the current game, for example
            this._historyGameCache.set(uuid, tx);
          }
          if (!this.isInitialized()) {
            this.isInitialized.set(true);
          }
        }),
        finalize(() => this.isLoading.set(false)),
      );
  }

  fetchCurrentTransaction(): Observable<Transaction | void> {
    const game = this.session.game();
    if (game) {
      this.isLoading.set(true);
      return this.http
        .get<Transaction>(
          environment.backendUrl + `/api/quiz/game/${game.uuid}/transaction`,
        )
        .pipe(
          tap((transaction) => {
            if (transaction) {
              this.currentTransaction.set(transaction);
              this.isTransactionEmpty.set(false);
              // TODO: clarify why we have empty quizLines, even though there is a game in the response
              if (transaction.quizGame || transaction.quizLines.length) {
                this.updateGameWithTransactionData(game, transaction);
                this.session.updateCurrentGameResult(true);
                this.session.removeGameFromStorage();
              }
            }
          }),
          tap(() => {
            this.isTransactionLoaded.set(true);
            this.isLoading.set(false);
          }),
          catchError((err) => {
            this.isTransactionLoaded.set(true);
            this.isLoading.set(false);
            // Rethrow the original HttpErrorResponse: it is already reported by
            // apiErrorInterceptor and filtered by GlobalErrorHandler, so wrapping
            // it in a plain Error would create duplicate Sentry noise.
            return throwError(() => err);
          }),
        );
    }
    this.isTransactionLoaded.set(true);
    return EMPTY;
  }

  changeIsTransactionEmpty(isEmpty: boolean): void {
    this.isTransactionEmpty.set(isEmpty);
  }

  private updateGameWithTransactionData(
    game: Quiz,
    transaction: Transaction,
  ): void {
    if (!transaction || !game || !game.questions?.length) return;
    const status = this.resultsService.getResultsStatus(transaction);
    const allPicks = transaction.quizLines.flatMap((line) => line.picks);
    const questions = game.questions.map((question) => {
      return this.transformQuestion(question, allPicks, status);
    });
    const lineWithJoker = allPicks.find((pick) => pick.isJokerUsed);
    if (lineWithJoker) {
      this.session.updateJoker(lineWithJoker.questionUuid);
    }
    const lineWithMultiplier = allPicks.find(
      (pick) => !!pick.multiplierOptionUuid,
    );
    if (lineWithMultiplier) {
      this.session.updateMultiplier({
        questionUuid: lineWithMultiplier.questionUuid,
        optionUuid: lineWithMultiplier.multiplierOptionUuid,
      });
    }
    this.session.setCurrentGame({
      ...game,
      questions,
    });
  }

  private transformTransaction(transaction: Transaction): Transaction {
    const status = this.resultsService.getResultsStatus(transaction);
    const lines = transaction.quizLines || [];
    const allPicks = lines.reduce((acc, line) => {
      return [...acc, ...line.picks];
    }, [] as LinePick[]);
    return {
      ...transaction,
      quizLines: transaction.quizLines.map((line) => {
        if (line.prize) {
          return {
            ...line,
            prize: {
              ...line.prize,
              label: overridePrizeLabel(line.prize, this.currencyService),
            },
          };
        }
        return line;
      }),
      quizGame: {
        ...transaction.quizGame,
        leaderboard: this.getLeaderboard(transaction.quizGame.leaderboard),
        prizePackage: this.getUpdatedPackage(transaction.quizGame.prizePackage),
        questions: transaction.quizGame.questions
          .map((question: Question) =>
            this.transformQuestion(question, allPicks, status),
          )
          .sort((a: Question, b: Question) => a.index - b.index),
      },
    };
  }

  private transformQuestion(
    question: Question,
    allPicks: LinePick[],
    status: ResultStatus,
  ): Question {
    const questionPick = allPicks.find(
      (pick) => pick.questionUuid === question.uuid,
    );
    // set utility properties for question options
    const sortedOptions = question.options
      .map((option) => {
        const isCorrect = isOptionCorrect(
          question.options,
          option.uuid,
          status,
        );
        // if it's a joker question, the correct options should be marked as picked
        const isPicked = questionPick?.isJokerUsed
          ? isCorrect
          : isOptionPicked(option, questionPick?.pickedUuid);
        return {
          ...option,
          isWrong: isOptionWrong(option, questionPick?.pickedUuid, status),
          isCorrect,
          isPicked,
        } as QuestionOption;
      })
      .sort((a: QuestionOption, b: QuestionOption) => a.index - b.index);
    if (question.type === QuestionType.CorrectScore) {
      return {
        ...question,
        isAnswerCorrect: isAnswerCorrect(
          question,
          questionPick?.pickedUuid,
          status,
        ),
        points: questionPick?.points ?? 0,
        competitor1Score: questionPick?.competitor1Score ?? undefined,
        competitor2Score: questionPick?.competitor2Score ?? undefined,
        options: sortedOptions,
      };
    }
    // check if there is jocker or multiplier used
    if (PICK_QUESTION_TYPES.includes(question.type)) {
      return {
        ...question,
        isAnswerCorrect: isAnswerCorrect(
          question,
          questionPick?.pickedUuid,
          status,
        ),
        options: sortedOptions,
      };
    }
    return {
      ...question,
      isAnswerCorrect: isAnswerCorrect(
        question,
        questionPick?.pickedUuid,
        status,
      ),
      options: sortedOptions,
    };
  }

  private getPickedOptions(
    transaction: Transaction | undefined,
  ): QuestionOption[][] | QuestionOption[] {
    if (!transaction || !transaction.quizGame) {
      return [];
    }
    const questions = transaction?.quizGame.questions || [];
    const quizLines = transaction?.quizLines || [];

    return questions.map((question) =>
      this.resultsService.processQuestion(question, quizLines),
    );
  }

  private getLeaderboard(leaderboard: Leaderboard | null): Leaderboard | null {
    if (!leaderboard) return null;
    const prizePackage = leaderboard.prizePackage
      ? {
          ...leaderboard.prizePackage,
          prizes: leaderboard.prizePackage.prizes
            .map((prize) => ({
              ...prize,
              prize: {
                ...prize.prize,
                label: overridePrizeLabel(prize.prize, this.currencyService),
              },
            }))
            .slice()
            .sort((a, b) => a.score - b.score),
        }
      : leaderboard.prizePackage;
    try {
      return {
        ...leaderboard,
        prizePackage,
      };
    } catch (e) {
      console.error("Can't update leaderboard", e);
      return null;
    }
  }

  private getUpdatedPackage(prizePackage: QuizPrizePackage): QuizPrizePackage {
    try {
      if (!prizePackage?.prizes) {
        return prizePackage;
      }

      const updatedPrizes = prizePackage.prizes.map((prize) => {
        if (typeof prize.prize.value === 'number' && prize.prize.currency) {
          return {
            ...prize,
            prize: {
              ...prize.prize,
              label: overridePrizeLabel(prize.prize, this.currencyService),
            },
          };
        }
        return prize;
      });

      const isPointsMode = prizePackage.mode === PrizePackageMode.Points;
      const sortedPrizes = [...updatedPrizes].sort((a, b) => {
        if (!isPointsMode) {
          return b.score - a.score;
        }
        return b.maxValue - a.maxValue;
      });

      return {
        ...prizePackage,
        prizes: sortedPrizes,
      };
    } catch (e) {
      console.error("Can't update prize package", e);
      return undefined as unknown as QuizPrizePackage;
    }
  }
}
